import { Fragment, useEffect, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { XIcon, ExclamationIcon } from "@heroicons/react/outline"
import { useMutation, useQueryClient } from "react-query"
import { useTranslation } from "react-i18next"
import { createOperatorRate, patchOperatorRate } from "../../../services/workforceServices"
import { extractBackendError, invalidateWorkforce } from "../../../utils/workforceUtils"

const CURRENCIES = ['MXN', 'BRL', 'USD']

const todayIso = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

export const RateFormSlidePanel = ({ mode, rate, warehouse_id, operatorTypes, contractTypes, onClose }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const [open, setOpen] = useState(false)
    const [values, setValues] = useState({
        operator_type: rate?.operator_type || '',
        contract_type: rate?.contract_type || '',
        gross_day_rate: rate?.gross_day_rate ?? '',
        gross_day_extra_hour_rate: rate?.gross_day_extra_hour_rate ?? '',
        gross_night_rate: rate?.gross_night_rate ?? '',
        gross_night_extra_hour_rate: rate?.gross_night_extra_hour_rate ?? '',
        currency: rate?.currency || 'MXN',
        effective_from: rate?.effective_from || todayIso(),
    })
    const [fieldErrors, setFieldErrors] = useState({})
    const [baseError, setBaseError] = useState('')
    const [confirmingFix, setConfirmingFix] = useState(false)

    useEffect(() => { setOpen(true) }, [])

    const setField = (k, v) => {
        setValues((prev) => ({ ...prev, [k]: v }))
        setFieldErrors((prev) => ({ ...prev, [k]: undefined }))
    }

    const handleClose = (didChange = false) => {
        setOpen(false)
        setTimeout(() => onClose(didChange), 300)
    }

    const createMutation = useMutation(
        (data) => createOperatorRate(warehouse_id, data),
        {
            onSuccess: () => {
                invalidateWorkforce(queryClient, warehouse_id, ['rates', 'costs', 'costs-range'])
                handleClose(true)
            },
            onError: (err) => applyBackendErrors(err),
        }
    )

    const patchMutation = useMutation(
        ({ id, data }) => patchOperatorRate(warehouse_id, id, data),
        {
            onSuccess: () => {
                invalidateWorkforce(queryClient, warehouse_id, ['rates', 'costs', 'costs-range'])
                handleClose(true)
            },
            onError: (err) => applyBackendErrors(err),
        }
    )

    const applyBackendErrors = (err) => {
        const { fields, base, message } = extractBackendError(err)
        setFieldErrors(fields)
        if (base.length) setBaseError(base.join(' · '))
        else if (!Object.keys(fields).length) setBaseError(message)
        else setBaseError('')
    }

    const validateClient = () => {
        const next = {}
        if (!values.operator_type) next.operator_type = t('workforce.rates.errors.required')
        if (!values.contract_type) next.contract_type = t('workforce.rates.errors.required')
        for (const f of ['gross_day_rate', 'gross_day_extra_hour_rate', 'gross_night_rate', 'gross_night_extra_hour_rate']) {
            if (values[f] === '' || values[f] === null || Number(values[f]) <= 0) {
                next[f] = t('workforce.rates.errors.positive')
            }
        }
        if (!values.currency) next.currency = t('workforce.rates.errors.required')
        if (!values.effective_from) next.effective_from = t('workforce.rates.errors.required')
        setFieldErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setBaseError('')
        if (!validateClient()) return
        const amounts = {
            gross_day_rate: Number(values.gross_day_rate),
            gross_day_extra_hour_rate: Number(values.gross_day_extra_hour_rate),
            gross_night_rate: Number(values.gross_night_rate),
            gross_night_extra_hour_rate: Number(values.gross_night_extra_hour_rate),
        }
        if (mode === 'fix') {
            if (!confirmingFix) { setConfirmingFix(true); return }
            patchMutation.mutate({ id: rate.id, data: amounts })
        } else {
            createMutation.mutate({
                operator_type: values.operator_type,
                contract_type: values.contract_type,
                ...amounts,
                currency: values.currency,
                effective_from: values.effective_from,
            })
        }
    }

    const isSubmitting = createMutation.isLoading || patchMutation.isLoading
    const title = mode === 'fix' ? t('workforce.rates.fix_title') : t('workforce.rates.add_title')

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-hidden z-20" open={open} onClose={() => handleClose(false)}>
                <div className="absolute inset-0 overflow-hidden">
                    <Dialog.Overlay className="absolute inset-0 bg-gray-700 bg-opacity-40" />
                    <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transform transition ease-in-out duration-300"
                            enterFrom="translate-x-full" enterTo="translate-x-0"
                            leave="transform transition ease-in-out duration-300"
                            leaveFrom="translate-x-0" leaveTo="translate-x-full"
                        >
                            <div className="w-screen max-w-lg">
                                <form onSubmit={handleSubmit} className="h-full flex flex-col bg-white shadow-xl">
                                    <div className="px-6 py-4 border-b flex items-center justify-between">
                                        <Dialog.Title className="text-lg font-medium text-gray-900">{title}</Dialog.Title>
                                        <button type="button" onClick={() => handleClose(false)} className="text-gray-400 hover:text-gray-500">
                                            <XIcon className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                                        {mode === 'fix' && (
                                            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 flex gap-2">
                                                <ExclamationIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                                                <div className="text-sm text-yellow-900">{t('workforce.rates.fix_warning')}</div>
                                            </div>
                                        )}

                                        {baseError && (
                                            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{baseError}</div>
                                        )}

                                        <Field label={t('workforce.rates.operator_type')} error={fieldErrors.operator_type}>
                                            <input
                                                type="text"
                                                list="operator-type-options"
                                                disabled={mode === 'fix'}
                                                value={values.operator_type}
                                                onChange={(e) => setField('operator_type', e.target.value)}
                                                onBlur={(e) => {
                                                    const normalized = e.target.value.trim().toUpperCase().replace(/\s+/g, '_')
                                                    if (normalized !== e.target.value) setField('operator_type', normalized)
                                                }}
                                                placeholder={t('workforce.rates.operator_type_placeholder')}
                                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white disabled:bg-gray-100"
                                            />
                                            <datalist id="operator-type-options">
                                                {operatorTypes.map((ot) => (<option key={ot} value={ot} />))}
                                            </datalist>
                                            {mode !== 'fix' && <p className="mt-1 text-xs text-gray-500">{t('workforce.rates.operator_type_hint')}</p>}
                                        </Field>

                                        <Field label={t('workforce.rates.contract')} error={fieldErrors.contract_type}>
                                            <select
                                                disabled={mode === 'fix'}
                                                value={values.contract_type}
                                                onChange={(e) => setField('contract_type', e.target.value)}
                                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white disabled:bg-gray-100"
                                            >
                                                <option value="">{t('workforce.rates.select_contract')}</option>
                                                {contractTypes.map((c) => (<option key={c} value={c}>{c}</option>))}
                                            </select>
                                        </Field>

                                        <Field label={t('workforce.rates.currency')} error={fieldErrors.currency}>
                                            <select
                                                value={values.currency}
                                                onChange={(e) => setField('currency', e.target.value)}
                                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                                            >
                                                {CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                                            </select>
                                        </Field>

                                        <fieldset className="rounded-md border border-gray-200 p-3">
                                            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-yellow-700">{t('workforce.rates.day_rates')}</legend>
                                            <div className="grid grid-cols-2 gap-3">
                                                <Field label={t('workforce.rates.day_daily_rate')} error={fieldErrors.gross_day_rate}>
                                                    <input
                                                        type="number" step="0.01" min="0"
                                                        value={values.gross_day_rate}
                                                        onChange={(e) => setField('gross_day_rate', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                                    />
                                                </Field>
                                                <Field label={t('workforce.rates.day_extra_hour_rate')} error={fieldErrors.gross_day_extra_hour_rate}>
                                                    <input
                                                        type="number" step="0.01" min="0"
                                                        value={values.gross_day_extra_hour_rate}
                                                        onChange={(e) => setField('gross_day_extra_hour_rate', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                                    />
                                                </Field>
                                            </div>
                                        </fieldset>

                                        <fieldset className="rounded-md border border-gray-200 p-3">
                                            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('workforce.rates.night_rates')}</legend>
                                            <div className="grid grid-cols-2 gap-3">
                                                <Field label={t('workforce.rates.night_daily_rate')} error={fieldErrors.gross_night_rate}>
                                                    <input
                                                        type="number" step="0.01" min="0"
                                                        value={values.gross_night_rate}
                                                        onChange={(e) => setField('gross_night_rate', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                                    />
                                                </Field>
                                                <Field label={t('workforce.rates.night_extra_hour_rate')} error={fieldErrors.gross_night_extra_hour_rate}>
                                                    <input
                                                        type="number" step="0.01" min="0"
                                                        value={values.gross_night_extra_hour_rate}
                                                        onChange={(e) => setField('gross_night_extra_hour_rate', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                                    />
                                                </Field>
                                            </div>
                                        </fieldset>

                                        <Field label={t('workforce.rates.effective_from')} error={fieldErrors.effective_from}>
                                            <input
                                                type="date"
                                                disabled={mode === 'fix'}
                                                value={values.effective_from}
                                                onChange={(e) => setField('effective_from', e.target.value)}
                                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 disabled:bg-gray-100"
                                            />
                                            {mode !== 'fix' && <p className="mt-1 text-xs text-gray-500">{t('workforce.rates.effective_from_hint')}</p>}
                                        </Field>
                                    </div>

                                    <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
                                        <button
                                            type="button"
                                            onClick={() => handleClose(false)}
                                            disabled={isSubmitting}
                                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {t('workforce.cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${mode === 'fix' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                                        >
                                            {isSubmitting
                                                ? t('workforce.saving')
                                                : mode === 'fix'
                                                    ? (confirmingFix ? t('workforce.rates.confirm_fix') : t('workforce.rates.review_fix'))
                                                    : t('workforce.rates.save_new')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

const Field = ({ label, error, children }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {children}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
)
